// DNA Pay - Conciliação
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  RefreshCw, AlertTriangle, CheckCircle, XCircle,
  Upload, FileText, Loader2, Search, Clock,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

export default function DnaPayConciliacao() {
  const [loading, setLoading] = useState(true);
  const [divergencias, setDivergencias] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [showConciliar, setShowConciliar] = useState(false);
  const [selectedDivergencia, setSelectedDivergencia] = useState(null);
  const [acao, setAcao] = useState('confirmar_pagamento');
  const [observacao, setObservacao] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadDivergencias();
  }, []);

  const loadDivergencias = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/dnapay/conciliacao/`);
      if (response.ok) {
        const data = await response.json();
        setDivergencias(data.divergencias || []);
        setResumo(data.resumo);
      }
    } catch (error) {
      toast.error('Erro ao carregar divergências');
    } finally {
      setLoading(false);
    }
  };

  const handleConciliar = async () => {
    if (!selectedDivergencia) return;

    try {
      const params = new URLSearchParams({
        titulo_id: selectedDivergencia.titulo_id,
        acao: acao,
        ...(observacao && { observacao })
      });

      const response = await fetch(`${API_URL}/api/dnapay/conciliacao/manual?${params}`, {
        method: 'POST'
      });

      if (response.ok) {
        toast.success('Conciliação realizada com sucesso!');
        setShowConciliar(false);
        setSelectedDivergencia(null);
        setObservacao('');
        loadDivergencias();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro na conciliação');
      }
    } catch (error) {
      toast.error('Erro ao conciliar');
    }
  };

  const handleUploadCnab = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('arquivo', file);

    try {
      const response = await fetch(`${API_URL}/api/dnapay/conciliacao/processar-cnab`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const resultado = await response.json();
        toast.success(`CNAB processado: ${resultado.total_confirmados} pagamentos confirmados`);
        loadDivergencias();
      } else {
        toast.error('Erro ao processar CNAB');
      }
    } catch (error) {
      toast.error('Erro ao enviar arquivo');
    } finally {
      setUploading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const getTipoBadge = (tipo) => {
    const config = {
      titulo_processamento_longo: { label: 'Processamento Longo', color: 'bg-yellow-100 text-yellow-700' },
      webhook_erro: { label: 'Webhook com Erro', color: 'bg-red-100 text-red-700' },
      titulo_nao_atualizado: { label: 'Título Desatualizado', color: 'bg-orange-100 text-orange-700' },
      titulo_vencido_antigo: { label: 'Vencido Antigo', color: 'bg-purple-100 text-purple-700' },
    };
    const cfg = config[tipo] || { label: tipo, color: 'bg-gray-100 text-gray-700' };
    return <Badge className={cfg.color}>{cfg.label}</Badge>;
  };

  return (
    <div className="space-y-6 p-6" data-testid="dnapay-conciliacao">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <RefreshCw className="w-7 h-7 text-purple-600" />
            Conciliação Financeira
          </h1>
          <p className="text-gray-500">Reconcilie pagamentos e resolva divergências</p>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadDivergencias}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          
          <label>
            <Button variant="outline" disabled={uploading} asChild>
              <span>
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Importar CNAB
              </span>
            </Button>
            <input type="file" accept=".ret,.txt" className="hidden" onChange={handleUploadCnab} />
          </label>
        </div>
      </div>

      {/* Resumo */}
      {resumo && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Divergências</p>
                  <p className="text-2xl font-bold">{resumo.total_divergencias}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {Object.entries(resumo.por_tipo || {}).map(([tipo, count]) => (
            <Card key={tipo}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <FileText className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 capitalize">{tipo.replace(/_/g, ' ')}</p>
                    <p className="text-2xl font-bold">{count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Lista de Divergências */}
      <Card>
        <CardHeader>
          <CardTitle>Divergências Encontradas</CardTitle>
          <CardDescription>Itens que necessitam de revisão manual</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            </div>
          ) : divergencias.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <CheckCircle className="w-12 h-12 mb-2 text-green-500" />
              <p className="font-medium">Tudo conciliado!</p>
              <p className="text-sm">Nenhuma divergência encontrada</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Valor</TableHead>
                  <TableHead>Ação Sugerida</TableHead>
                  <TableHead className="text-right">Ação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {divergencias.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{getTipoBadge(item.tipo)}</TableCell>
                    <TableCell>
                      <p className="font-medium">{item.descricao}</p>
                      {item.titulo_id && <p className="text-xs text-gray-500">ID: {item.titulo_id?.slice(0, 8)}</p>}
                    </TableCell>
                    <TableCell>{item.valor ? formatCurrency(item.valor) : '-'}</TableCell>
                    <TableCell className="text-sm text-gray-600">{item.acao_sugerida}</TableCell>
                    <TableCell className="text-right">
                      {item.titulo_id && (
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedDivergencia(item);
                            setShowConciliar(true);
                          }}
                        >
                          Conciliar
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modal Conciliar */}
      <Dialog open={showConciliar} onOpenChange={setShowConciliar}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conciliação Manual</DialogTitle>
            <DialogDescription>
              {selectedDivergencia?.descricao}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Ação</Label>
              <Select value={acao} onValueChange={setAcao}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirmar_pagamento">Confirmar Pagamento</SelectItem>
                  <SelectItem value="cancelar">Cancelar Título</SelectItem>
                  <SelectItem value="reverter_pendente">Reverter para Pendente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Observação</Label>
              <Input
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                placeholder="Motivo da conciliação..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConciliar(false)}>Cancelar</Button>
            <Button onClick={handleConciliar}>Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
